import { useEffect } from "react";
import { localeFromHostname } from "@pracownik/domain";

export type BrandKey = "pracownik" | "teammember";

export interface Brand {
  key: BrandKey;
  /** Marketing name as written in copy. */
  name: "pracownik.ai" | "teammember.ai";
  /** The two visual halves of the wordmark. */
  wordmark: [string, string];
  locale: "pl" | "en";
  domain: "pracownik.ai" | "teammember.ai";
  appHost: string;
  landingUrl: string;
  supportEmail: string;
  requestAccessUrl: string;
  company: string;
}

const BRANDS: Record<BrandKey, Brand> = {
  pracownik: {
    key: "pracownik",
    name: "pracownik.ai",
    wordmark: ["pracownik", ".ai"],
    locale: "pl",
    domain: "pracownik.ai",
    appHost: "app.pracownik.ai",
    landingUrl: "https://pracownik.ai",
    supportEmail: "obsluga@pracownik.ai",
    requestAccessUrl: "https://pracownik.ai/#waitlist",
    company: "Wondel.ai sp. z o.o.",
  },
  teammember: {
    key: "teammember",
    name: "teammember.ai",
    wordmark: ["teammember", ".ai"],
    locale: "en",
    domain: "teammember.ai",
    appHost: "app.teammember.ai",
    landingUrl: "https://teammember.ai",
    supportEmail: "support@teammember.ai",
    requestAccessUrl: "https://teammember.ai/#waitlist",
    company: "Wondel.ai sp. z o.o.",
  },
};

function isPreviewHost(hostname: string): boolean {
  if (import.meta.env.DEV) return true;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "mac-mini" ||
    hostname.endsWith(".pages.dev")
  );
}

/** The brand is decided by the hostname, never by a user setting. */
export function resolveBrand(
  hostname = typeof window === "undefined" ? "" : window.location.hostname,
): Brand {
  // Local development has no brand host; `?brand=teammember` previews the EN
  // brand. Production hosts ignore it so a crafted link can't restyle the app.
  const override = isPreviewHost(hostname)
    ? new URLSearchParams(window.location.search).get("brand")
    : null;
  if (override === "teammember" || override === "pracownik") return BRANDS[override];
  return localeFromHostname(hostname) === "pl" ? BRANDS.pracownik : BRANDS.teammember;
}

export const brand: Brand = resolveBrand();

/** Reflect the brand onto <html> so CSS tokens, lang and the tab title follow it. */
export function applyBrandToDocument(b: Brand = brand): void {
  const root = document.documentElement;
  root.dataset.brand = b.key;
  root.lang = b.locale;
}

export function useDocumentTitle(title?: string): void {
  useEffect(() => {
    document.title = title ? `${title} · ${brand.name}` : brand.name;
  }, [title]);
}
